import {defineStore} from 'pinia'

const name = 'pappusAi'
const STORAGE_KEY = 'pappusAi.messages'
const WIDTH_KEY = 'pappusAi.width'
// Conversation en cours : son identifiant sur le serveur
const SESSION_KEY = 'pappusAi.session'

function load(key, fallback) {
  try {
    const value = localStorage.getItem(key)
    return value ? JSON.parse(value) : fallback
  } catch (e) {
    return fallback
  }
}

function save(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (e) {
    // Stockage indisponible : on garde l'état en mémoire
  }
}

export const usePappusAiStore = defineStore(name, {

  id: name,

  state: () => ({
    drawerOpen: false,
    // Rouvre le chat au retour sur l'accueil (après un clic sur un lien vers une fiche)
    reopenOnReturn: false,
    maximized: false,
    width: load(WIDTH_KEY, 480),
    // {role: 'user'|'assistant', content, sources: [], links: {'type:id': nom}, fiches: [{lien, nom}], error}
    messages: load(STORAGE_KEY, []),
    // Conversation en cours, enregistrée sur le serveur : son identifiant (null tant qu'elle ne l'est pas)
    sessionId: load(SESSION_KEY, null),
    // Historique : conversations enregistrées de l'utilisateur, la plus récente d'abord
    sessions: [],
    sessionsLoading: false,
    status: '',
    // Suggestions : diagnostics de la base et dernières fiches consultées
    context: {diagnostics: [], recent: []},
    isStreaming: false,
    abortController: null
  }),

  actions: {
    openDrawer() {
      this.drawerOpen = true
    },
    closeDrawer() {
      this.drawerOpen = false
    },
    toggleMaximize() {
      this.maximized = !this.maximized
    },
    setWidth(width) {
      this.width = width
      save(WIDTH_KEY, width)
    },
    /** Nouvelle conversation : la précédente reste dans l'historique */
    clear() {
      this.stop()
      this.messages = []
      this.sessionId = null
      save(STORAGE_KEY, this.messages)
      save(SESSION_KEY, null)
    },
    /** Déconnexion, ou autre utilisateur sur le même navigateur : rien de la conversation ne reste sur l'appareil */
    reset() {
      this.clear()
      this.sessions = []
      this.drawerOpen = false
    },

    /* ---------- Conversations enregistrées sur le serveur ---------- */

    /**
     * Enregistre la conversation en cours sur le serveur (création à la première réponse, puis mise à jour).
     * Sans bloquer ni gêner le chat : en cas d'échec, la conversation reste sur l'appareil et sera enregistrée
     * à la réponse suivante.
     */
    async persist() {
      if (!this.messages.some((m) => m.role === 'user' && m.content)) return
      // Un enregistrement à la fois : le suivant attend (sinon deux créations pour une même conversation)
      const previous = this.saving || Promise.resolve()
      this.saving = previous.then(async () => {
        try {
          const {default: PappusAiService} = await import("src/services/pappusAiService")
          const session = await PappusAiService.saveSession(this.sessionId, this.messages)
          this.sessionId = session.id
          save(SESSION_KEY, session.id)
        } catch (e) {
          // Conversation supprimée entre-temps sur un autre appareil : elle sera recréée
          if (/introuvable/i.test(e.message || '')) {
            this.sessionId = null
            save(SESSION_KEY, null)
          }
        }
      })
      return this.saving
    },
    async loadSessions() {
      this.sessionsLoading = true
      try {
        const {default: PappusAiService} = await import("src/services/pappusAiService")
        this.sessions = await PappusAiService.sessions()
      } catch (e) {
        this.sessions = []
      } finally {
        this.sessionsLoading = false
      }
    },
    /** Rouvre une conversation de l'historique : elle devient la conversation en cours */
    async openSession(id) {
      this.stop()
      const {default: PappusAiService} = await import("src/services/pappusAiService")
      const session = await PappusAiService.session(id)
      this.messages = session.messages
      this.sessionId = session.id
      save(STORAGE_KEY, this.messages)
      save(SESSION_KEY, session.id)
    },
    async renameSession(id, title) {
      const {default: PappusAiService} = await import("src/services/pappusAiService")
      const renamed = await PappusAiService.renameSession(id, title)
      this.sessions = this.sessions.map((session) => (session.id === id ? {...session, ...renamed} : session))
    },
    async deleteSession(id) {
      const {default: PappusAiService} = await import("src/services/pappusAiService")
      await PappusAiService.deleteSession(id)
      this.sessions = this.sessions.filter((session) => session.id !== id)
      // Conversation en cours supprimée : on repart d'une conversation vide
      if (this.sessionId === id) this.clear()
    },
    stop() {
      if (this.abortController) this.abortController.abort()
    },
    /**
     * Conseil créé depuis le chat, affiché dans le fil (éditeur). Sans texte :
     * il n'est pas envoyé à l'IA avec l'historique de la conversation.
     */
    addAdvice(adviceId) {
      this.messages.push({role: 'assistant', content: '', adviceId})
      save(STORAGE_KEY, this.messages)
      this.persist()
    },

    async loadContext() {
      try {
        const {default: PappusAiService} = await import("src/services/pappusAiService")
        this.context = await PappusAiService.context()
      } catch (e) {
        // Suggestions contextuelles indisponibles : les suggestions fixes restent
      }
    },

    /**
     * @param {string} text
     * @param {{baseOnly?: boolean, scope?: string}} options - baseOnly : question issue d'une
     *        suggestion, réponse limitée au référentiel Pappus ; scope : module interrogé
     */
    async send(text, {baseOnly = false, scope = null} = {}) {
      const content = (text || '').trim()
      if (!content || this.isStreaming) return

      this.messages.push({role: 'user', content, baseOnly: baseOnly || !!scope, scope})
      const history = this.messages.map(m => ({role: m.role, content: m.content}))
      this.messages.push({role: 'assistant', content: '', sources: [], links: {}})
      const answer = this.messages[this.messages.length - 1]

      this.isStreaming = true
      this.status = 'Réflexion…'
      this.abortController = new AbortController()

      // Regroupe les morceaux de texte reçus : un seul rendu markdown par image
      // (~60/s) au lieu d'un rendu complet de la réponse à chaque morceau
      let pendingText = ''
      let frame = null
      const flushText = () => {
        frame = null
        if (pendingText) {
          answer.content += pendingText
          pendingText = ''
        }
      }

      try {
        // Import à la demande : un import statique crée un cycle
        // (service -> axios -> router -> dashBoard -> ce store)
        const {default: PappusAiService} = await import("src/services/pappusAiService")
        await PappusAiService.chat(history, (event) => {
          switch (event.type) {
            case 'text':
              pendingText += event.text
              if (!frame) frame = requestAnimationFrame(flushText)
              this.status = ''
              break
            case 'status':
              this.status = event.text
              break
            case 'links':
              Object.assign(answer.links, event.links)
              break
            case 'sources':
              answer.sources = event.sources
              break
            // Fiches de la base utilisées pour la réponse (affichées sous la réponse)
            case 'fiches':
              answer.fiches = event.fiches
              break
            case 'error':
              answer.error = event.message
              break
          }
        }, this.abortController.signal, {baseOnly, scope})
      } catch (e) {
        if (e.name !== 'AbortError') {
          answer.error = 'Pappus est injoignable. Vérifie ta connexion.'
        }
      } finally {
        // Onglet en arrière-plan : requestAnimationFrame est suspendu, on vide le reste ici
        if (frame) cancelAnimationFrame(frame)
        flushText()
        if (!answer.content && !answer.error) {
          answer.error = 'Réponse interrompue.'
        }
        this.isStreaming = false
        this.status = ''
        this.abortController = null
        save(STORAGE_KEY, this.messages)
        this.persist()
      }
    }
  }
})
